import React, { useEffect, useState } from 'react';
import { menuAPI, ALLERGENS, ALLERGENS_BY_CODE } from '../../lib/api';
import { getDayName, isToday } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { toast } from 'sonner';
import { Plus, Trash2, Save, Calendar, X } from 'lucide-react';

const MealSection = ({ mealKey, title, items, onAdd, onUpdate, onRemove, onToggleAllergen }) => (
  <div className="border border-slate-200 rounded-xl p-3 space-y-2 bg-slate-50/50" data-testid={`meal-section-${mealKey}`}>
    <div className="flex items-center justify-between">
      <Label className="text-base font-semibold text-[#2C3E50]">{title}</Label>
      <Button type="button" size="sm" variant="outline" onClick={onAdd} className="rounded-full text-xs" data-testid={`add-${mealKey}`}>
        <Plus size={12} className="mr-1" /> Přidat jídlo
      </Button>
    </div>
    {items.length === 0 && (
      <div className="text-xs text-[#94A3B8] text-center py-2">Zatím žádné jídlo. Klikněte na „Přidat jídlo".</div>
    )}
    {items.map((meal, i) => (
      <div key={i} className="flex items-start gap-2 p-2 bg-white rounded-lg border border-slate-100" data-testid={`${mealKey}-item-${i}`}>
        <Input
          value={meal.name || ''}
          onChange={(e) => onUpdate(i, 'name', e.target.value)}
          placeholder="Název jídla (např. Kuřecí řízek s bramborem)"
          className="rounded-lg flex-1 min-w-[160px]"
          data-testid={`${mealKey}-name-${i}`}
        />
        <Popover>
          <PopoverTrigger asChild>
            <Button type="button" size="sm" variant="outline" className="rounded-lg flex-shrink-0" data-testid={`${mealKey}-allergens-btn-${i}`}>
              Alergeny {meal.allergens?.length > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[#4ECDC4] text-white text-[10px]">{meal.allergens.length}</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-2" align="end">
            <div className="text-xs font-medium mb-2 text-[#64748B]">Vyberte přítomné alergeny</div>
            <div className="max-h-64 overflow-y-auto space-y-1">
              {ALLERGENS.map(a => {
                const active = meal.allergens?.includes(a.code);
                return (
                  <button
                    key={a.code}
                    type="button"
                    onClick={() => onToggleAllergen(i, a.code)}
                    className={`w-full text-left px-2 py-1.5 rounded-md text-xs flex items-center gap-2 transition-colors ${active ? 'bg-[#4ECDC4] text-white' : 'hover:bg-slate-100 text-[#2D3748]'}`}
                    data-testid={`${mealKey}-allergen-${i}-${a.code}`}
                  >
                    <span className="font-mono font-bold w-6">{a.code}</span>
                    <span className="flex-1">{a.label}</span>
                    {active && <X size={12} />}
                  </button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
        <Button type="button" size="sm" variant="ghost" onClick={() => onRemove(i)} className="text-red-500 hover:text-red-700 flex-shrink-0" data-testid={`${mealKey}-remove-${i}`}>
          <Trash2 size={14} />
        </Button>
      </div>
    ))}
  </div>
);
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

export const MenuAdmin = () => {
  const [menu, setMenu] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    date: '',
    breakfast_items: [],
    snack_am_items: [],
    lunch_items: [],
    snack_pm_items: [],
    notes: '',
    site: 'ms'
  });

  useEffect(() => {
    loadMenu();
  }, []);

  const loadMenu = async () => {
    try {
      const response = await menuAPI.getAll();
      setMenu(response.data);
    } catch (error) {
      toast.error('Nepodařilo se načíst jídelníček');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await menuAPI.delete(deleteId);
      setMenu(menu.filter(m => m.id !== deleteId));
      toast.success('Položka byla smazána');
    } catch (error) {
      toast.error('Nepodařilo se smazat položku');
    } finally {
      setDeleteId(null);
    }
  };

  // Konvertuje legacy textové pole na strukturované items (backward compat)
  const legacyToItems = (text) => text ? text.split(',').map(s => ({ name: s.trim(), allergens: [] })).filter(i => i.name) : [];

  const itemsFor = (item, key) => {
    const structured = item?.[`${key}_items`];
    if (Array.isArray(structured) && structured.length > 0) return structured;
    return legacyToItems(item?.[key]);
  };

  const openAddDialog = () => {
    setEditingItem(null);
    setFormData({
      date: '',
      breakfast_items: [],
      snack_am_items: [],
      lunch_items: [],
      snack_pm_items: [],
      notes: '',
      site: 'ms'
    });
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      date: item.date,
      breakfast_items: itemsFor(item, 'breakfast'),
      snack_am_items: itemsFor(item, 'snack_am'),
      lunch_items: itemsFor(item, 'lunch'),
      snack_pm_items: itemsFor(item, 'snack_pm'),
      notes: item.notes || '',
      site: item.site || 'ms'
    });
    setDialogOpen(true);
  };

  const addMeal = (mealKey) => {
    setFormData(prev => ({ ...prev, [mealKey]: [...(prev[mealKey] || []), { name: '', allergens: [] }] }));
  };
  const updateMeal = (mealKey, idx, field, value) => {
    setFormData(prev => {
      const arr = [...(prev[mealKey] || [])];
      arr[idx] = { ...arr[idx], [field]: value };
      return { ...prev, [mealKey]: arr };
    });
  };
  const removeMeal = (mealKey, idx) => {
    setFormData(prev => ({ ...prev, [mealKey]: (prev[mealKey] || []).filter((_, i) => i !== idx) }));
  };
  const toggleAllergen = (mealKey, idx, code) => {
    setFormData(prev => {
      const arr = [...(prev[mealKey] || [])];
      const current = arr[idx]?.allergens || [];
      const next = current.includes(code) ? current.filter(c => c !== code) : [...current, code];
      arr[idx] = { ...arr[idx], allergens: next };
      return { ...prev, [mealKey]: arr };
    });
  };

  const buildPayload = () => {
    const cleanItems = (arr) => (arr || []).filter(i => i.name?.trim()).map(i => ({ name: i.name.trim(), allergens: i.allergens || [] }));
    const toText = (arr) => arr.map(i => i.name).join(', ');
    const b = cleanItems(formData.breakfast_items);
    const sa = cleanItems(formData.snack_am_items);
    const l = cleanItems(formData.lunch_items);
    const sp = cleanItems(formData.snack_pm_items);
    return {
      date: formData.date,
      breakfast_items: b,
      snack_am_items: sa,
      lunch_items: l,
      snack_pm_items: sp,
      // Legacy textová pole udržujeme synchronizovaná — starší část webu je stále čte
      breakfast: toText(b),
      snack_am: toText(sa),
      lunch: toText(l) || ' ',  // legacy pole `lunch` bylo dřív povinné
      snack_pm: toText(sp),
      notes: formData.notes,
      site: formData.site || 'ms'
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = buildPayload();
      if (editingItem) {
        await menuAPI.update(editingItem.id, payload);
        setMenu(menu.map(m => m.id === editingItem.id ? { ...m, ...payload } : m));
        toast.success('Položka byla aktualizována');
      } else {
        const response = await menuAPI.create(payload);
        setMenu([...menu, response.data].sort((a, b) => a.date.localeCompare(b.date)));
        toast.success('Položka byla přidána');
      }
      setDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nepodařilo se uložit položku');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#2C3E50]">Jídelníček</h1>
          <p className="text-[#64748B]">Správa týdenního jídelníčku</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openAddDialog} className="rounded-full bg-[#4ECDC4] hover:bg-[#3DBDB5]" data-testid="add-menu-item">
              <Plus size={18} className="mr-2" />
              Přidat den
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Upravit jídelníček' : 'Nový den'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="date">Datum *</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  required
                  disabled={!!editingItem}
                  className="rounded-xl"
                  data-testid="menu-date"
                />
                <p className="text-xs text-[#64748B]">Jídelníček je sdílený pro obě školky.</p>
              </div>
              <MealSection mealKey="breakfast_items" title="Snídaně" items={formData.breakfast_items} onAdd={() => addMeal('breakfast_items')} onUpdate={(i, f, v) => updateMeal('breakfast_items', i, f, v)} onRemove={(i) => removeMeal('breakfast_items', i)} onToggleAllergen={(i, c) => toggleAllergen('breakfast_items', i, c)} />
              <MealSection mealKey="snack_am_items" title="Dopolední svačina" items={formData.snack_am_items} onAdd={() => addMeal('snack_am_items')} onUpdate={(i, f, v) => updateMeal('snack_am_items', i, f, v)} onRemove={(i) => removeMeal('snack_am_items', i)} onToggleAllergen={(i, c) => toggleAllergen('snack_am_items', i, c)} />
              <MealSection mealKey="lunch_items" title="Oběd" items={formData.lunch_items} onAdd={() => addMeal('lunch_items')} onUpdate={(i, f, v) => updateMeal('lunch_items', i, f, v)} onRemove={(i) => removeMeal('lunch_items', i)} onToggleAllergen={(i, c) => toggleAllergen('lunch_items', i, c)} />
              <MealSection mealKey="snack_pm_items" title="Odpolední svačina" items={formData.snack_pm_items} onAdd={() => addMeal('snack_pm_items')} onUpdate={(i, f, v) => updateMeal('snack_pm_items', i, f, v)} onRemove={(i) => removeMeal('snack_pm_items', i)} onToggleAllergen={(i, c) => toggleAllergen('snack_pm_items', i, c)} />
              <div className="space-y-2">
                <Label htmlFor="notes">Poznámka</Label>
                <Input
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="rounded-xl"
                  data-testid="menu-notes"
                />
              </div>
              <div className="flex gap-4 pt-4">
                <Button type="submit" className="flex-1 rounded-full bg-[#4ECDC4] hover:bg-[#3DBDB5]" data-testid="menu-save">
                  <Save size={16} className="mr-2" />
                  {editingItem ? 'Uložit' : 'Přidat'}
                </Button>
                <Button type="button" variant="outline" className="flex-1 rounded-full" onClick={() => setDialogOpen(false)}>
                  Zrušit
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {menu.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {menu.map((item) => {
            const today = isToday(item.date);
            return (
              <div 
                key={item.id} 
                className={`bg-white rounded-2xl border p-5 ${today ? 'border-[#4ECDC4] ring-2 ring-[#4ECDC4]/20' : 'border-slate-100'}`}
                data-testid={`menu-item-${item.id}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className={`font-semibold ${today ? 'text-[#4ECDC4]' : 'text-[#2C3E50]'}`}>
                      {getDayName(item.date)}
                    </div>
                    <div className="text-xs text-[#64748B]">
                      {new Date(item.date).toLocaleDateString('cs-CZ')}
                    </div>
                  </div>
                  {today && (
                    <span className="px-2 py-1 rounded-full bg-[#4ECDC4] text-white text-xs font-medium">
                      Dnes
                    </span>
                  )}
                </div>
                
                <div className="space-y-2 text-sm">
                  {item.breakfast && (
                    <div>
                      <span className="text-[#64748B]">Snídaně:</span>
                      <span className="ml-1 text-[#2C3E50]">{item.breakfast}</span>
                    </div>
                  )}
                  {item.snack_am && (
                    <div>
                      <span className="text-[#64748B]">Svačina:</span>
                      <span className="ml-1 text-[#2C3E50]">{item.snack_am}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-[#64748B]">Oběd:</span>
                    <span className="ml-1 text-[#2C3E50] font-medium">{item.lunch}</span>
                  </div>
                  {item.snack_pm && (
                    <div>
                      <span className="text-[#64748B]">Svačina:</span>
                      <span className="ml-1 text-[#2C3E50]">{item.snack_pm}</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 rounded-full text-xs"
                    onClick={() => openEditDialog(item)}
                  >
                    Upravit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => setDeleteId(item.id)}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-20 bg-white rounded-2xl border border-slate-100">
          <Calendar size={48} className="mx-auto text-[#CBD5E1] mb-4" />
          <div className="text-[#64748B]">Žádné položky jídelníčku</div>
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Smazat položku?</AlertDialogTitle>
            <AlertDialogDescription>Tato akce je nevratná.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Zrušit</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Smazat</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MenuAdmin;
